import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import type { Feature, FeatureCollection } from 'geojson';

import { client } from '@/modules/api/client.gen';

const QueryKey = {
  root: ['features'],

  collection: ({ collection }: { collection: FeatureCollectionName }) => [...QueryKey.root, collection],

  query: ({ collection, query }: { collection: FeatureCollectionName; query?: Record<string, unknown> }) => [
    ...QueryKey.collection({ collection }),
    query,
  ],
};

const requestFeatures = async <T extends FeatureCollection>({
  collection,
  query,
}: Omit<UseFeaturesQueryProps, 'enabled'>) => {
  const baseUrl = client.getConfig().baseUrl ?? window.location.origin;
  const url = new URL(`/features/collections/public.${collection}/items.json`, baseUrl);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  const response = await fetch(url);

  if (response.ok) {
    return (await response.json()) as T;
  }

  const error = new Error(response.statusText) as Error & { status: number };
  error.status = response.status;

  throw error;
};

export const requestFeature = async <T extends Feature>({
  collection,
  query,
}: Omit<UseFeaturesQueryProps, 'enabled'>) => {
  const featureCollection = await requestFeatures<FeatureCollection<T['geometry'], T['properties']>>({
    collection,
    query,
  });

  const result = featureCollection.features[0];

  if (!result) {
    const error = new Error('Not found') as Error & { status: number };
    error.status = 404;
    throw error;
  }

  return result as T;
};

type FeatureCollectionName = 'report_view' | 'route_sign_view' | 'admin_level_view' | 'service_station_view';

interface UseFeaturesQueryProps {
  collection: FeatureCollectionName;
  query?: Record<string, unknown>;
  enabled?: boolean;
}

export const useFeaturesQuery = <T extends FeatureCollection>({ collection, query, enabled }: UseFeaturesQueryProps) =>
  useQuery({
    queryKey: QueryKey.query({ collection, query }),
    queryFn: async () => requestFeatures<T>({ collection, query }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const featureQueryOptions = <T extends Feature>({ collection, query }: Omit<UseFeaturesQueryProps, 'enabled'>) =>
  queryOptions({
    queryKey: QueryKey.query({ collection, query }),
    queryFn: async () => requestFeature<T>({ collection, query }),
  });

export const useFeatureQuery = <T extends Feature>({ collection, query, enabled }: UseFeaturesQueryProps) =>
  useQuery({
    ...featureQueryOptions<T>({ collection, query }),
    placeholderData: keepPreviousData,
    enabled,
  });
